/**
 * Copyright (c) 2024 rports (r), All rights reserved.
 *
 *     ______    _______  _______  ______    _______  _______ 
 *    |    _ |  |       ||       ||    _ |  |       ||       |
 *    |   | ||  |    _  ||   _   ||   | ||  |_     _||  _____|
 *    |   |_||_ |   |_| ||  | |  ||   |_||_   |   |  | |_____ 
 *    |    __  ||    ___||  |_|  ||    __  |  |   |  |_____  |
 *    |   |  | ||   |    |       ||   |  | |  |   |   _____| |
 *    |___|  |_||___|    |_______||___|  |_|  |___|  |_______|
 *
 * filename      : Auth.jsx
 * description   : {{insert here}}
 * creation date : 10/02/2024
 * author        : "Luis CRUZ" <luis.cruz@rports.net>
 */



// --------------------------------------------------------------------------------
//      IMPORTS
// --------------------------------------------------------------------------------

import {
    useEffect,
    useState,
    Fragment,
} from 'react';

import {
    I18n,
} from '@aws-amplify/core';

import {
    Authenticator,
    useAuthenticator,
    View,
    Heading,
    Flex,
} from '@aws-amplify/ui-react';

import {
    auth_props,
} from '@/utils/auth/core';

import {
    useComponentContext,
    toolKit,
} from '@/components/core';

import {
    auth_routes,
    route_list,
    search_list,
} from '@/utils/router/core';

import {
    locale_msg,
} from '@/utils/locale/enums';

import {
    LinkButton,
} from '@/components/snippets/RouterLink';

import {
    LuLogIn as IconLogIn,
} from 'react-icons/lu';

import {
    MdOutlineCancel as IconCancel,
} from 'react-icons/md';



// --------------------------------------------------------------------------------
//      FUNCTIONS
// --------------------------------------------------------------------------------

function Auth() {
    // Component context
    const { ctx } = useComponentContext();

    // Authenticator routes
    const { toSignIn, toSignUp, toResetPassword } = useAuthenticator();

    // State variables
    const [isDone, set_isDone] = useState(false);
    const [path, set_path] = useState(undefined);
    const [authLocalProps, set_authLocalProps] = useState({});

    // Listen asynchronous events: Sign in complete
    useEffect(() => {
        // Detect sign in event
        if (
            (!isDone) &&
            (ctx.auth.state === toolKit.enums.auth.stateTypes.AUTHENTICATED)
        ) {
            set_isDone(true);
        }

        // Detect sign out event
        if (
            (isDone) &&
            (ctx.auth.state === toolKit.enums.auth.stateTypes.UNAUTHENTICATED)
        ) {
            set_isDone(false);
        }

        // Navigate to the 'returnTo' path, if any
        if (
            (isDone) &&
            (ctx.auth.state === toolKit.enums.auth.stateTypes.AUTHENTICATED)
        ) {
            const { search } = ctx.api.location.getLocation();

            if (search.hasOwnProperty(search_list.RETURN_TO)) {
                ctx.api.location.navigate(
                    search[search_list.RETURN_TO]
                );
            }
        }
    },
        [
            isDone,
            ctx,
        ]
    );

    // Listen asynchronous events: Authentication path for local properties
    useEffect(() => {
        // Get the authentication route
        const new_path = ctx.api.location.getPath();

        // Detect path changes
        if (path !== new_path) {
            // Set the authenticator local properties
            switch (new_path) {
                case route_list.AUTH_SIGN_UP:
                    set_authLocalProps({
                        hideSignUp: false,
                        initialState: 'signUp',
                        className: 'data-amplify-authenticator-route-signup',
                    });
                    break;

                case route_list.AUTH_RESET_PASSWORD:
                    set_authLocalProps({
                        hideSignUp: true,
                        initialState: 'resetPassword',
                        className: 'data-amplify-authenticator-route-resetpassword',
                    });
                    break;

                case route_list.AUTH_SIGN_IN:
                default:
                    set_authLocalProps({
                        hideSignUp: true,
                        initialState: 'signIn',
                        className: 'data-amplify-authenticator-route-signin',
                    });
                    break;
            }

            // Set the new path
            set_path(new_path);
        }
    },
        [
            ctx,
            path,
        ]
    );

    // Listen asynchronous events: Authentication path for routes
    useEffect(() => {
        if (
            (path !== undefined) &&
            (ctx.auth.state === toolKit.enums.auth.stateTypes.UNAUTHENTICATED)
        ) {
            // Set the authenticator local properties
            switch (path) {
                case route_list.AUTH_SIGN_UP:
                    toSignUp();
                    break;

                case route_list.AUTH_RESET_PASSWORD:
                    toResetPassword();
                    break;

                case route_list.AUTH_SIGN_IN:
                default:
                    toSignIn();
                    break;
            }
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            path,
        ]
    );

    return (
        <Fragment>
            {/* Is authenticated */}
            {(ctx.auth.state === toolKit.enums.auth.stateTypes.AUTHENTICATED) &&
                <Fragment>
                    <View
                        textAlign='center'
                    >
                        <Heading
                            level={4}
                            paddingBottom={ctx.theme.tokens.space.large}
                        >
                            {/* TODO: Translate message */}
                            Welcome <i><b>{ctx.auth.user.attributes.name}</b></i>
                        </Heading>

                        <Flex
                            direction='column'
                            justifyContent='center'
                            alignItems='center'
                            alignContent='center'
                            wrap='wrap'
                            gap={ctx.theme.tokens.space.zero}
                        >
                            <View
                                style={{
                                    color: ctx.theme.tokens.colors.font.success
                                }}
                                backgroundColor={ctx.theme.tokens.colors.background.success}
                                padding={ctx.theme.tokens.space.large}
                                borderRadius={ctx.theme.tokens.radii.large}
                            >
                                <IconLogIn
                                    size={ctx.theme.tokens.space.xxxl.value}
                                />
                            </View>

                            <LinkButton
                                to={
                                    toolKit.api.location.buildUrl(
                                        route_list.HOME
                                    )
                                }
                                fontWeight='normal'
                                size='small'
                                variation='link'
                                marginTop={ctx.theme.tokens.space.large}
                            >
                                {/* TODO: Translate message */}
                                Back to home page
                            </LinkButton>
                        </Flex>
                    </View>
                </Fragment>
            }

            {/* Is NOT authenticated */}
            {(ctx.auth.state === toolKit.enums.auth.stateTypes.UNAUTHENTICATED) &&
                <Fragment>
                    {/* Valid routes */}
                    {(
                        (path === route_list.AUTH_SIGN_IN) ||
                        (path === route_list.AUTH_RESET_PASSWORD)
                    ) &&
                        <Fragment>
                            <Authenticator
                                {...auth_props}
                                {...authLocalProps}
                            />
                        </Fragment>
                    }

                    {/* Invalid routes */}
                    {(path === route_list.AUTH_SIGN_UP) &&
                        <Fragment>
                            {/* TODO: Include registration/contact page link */}
                            <View
                                textAlign='center'
                            >
                                <Heading
                                    level={4}
                                    paddingBottom={ctx.theme.tokens.space.large}
                                >
                                    {/* TODO: Translate message */}
                                    Registration is currently unavailable
                                </Heading>

                                <Flex
                                    direction='column'
                                    justifyContent='center'
                                    alignItems='center'
                                    alignContent='center'
                                    wrap='wrap'
                                    gap={ctx.theme.tokens.space.zero}
                                >
                                    <View
                                        style={{
                                            color: ctx.theme.tokens.colors.font.error
                                        }}
                                        backgroundColor={ctx.theme.tokens.colors.background.error}
                                        padding={ctx.theme.tokens.space.large}
                                        borderRadius={ctx.theme.tokens.radii.large}
                                    >
                                        <IconCancel
                                            size={ctx.theme.tokens.space.xxxl.value}
                                        />
                                    </View>

                                    <LinkButton
                                        to={
                                            ctx.api.utils.buildUrlAuth(
                                                auth_routes.SIGN_IN
                                            )
                                        }
                                        fontWeight='normal'
                                        size='small'
                                        variation='link'
                                        marginTop={ctx.theme.tokens.space.large}
                                        className='custom-button-link'
                                    >
                                        {I18n.get(locale_msg.COGNITO_AUTH__CREATE_ACCOUNT__BACK_TO_SIGN_IN)}
                                    </LinkButton>
                                </Flex>
                            </View>
                        </Fragment>
                    }
                </Fragment>
            }
        </Fragment>
    );
}



// --------------------------------------------------------------------------------
//      EXPORTED OBJECTS
// --------------------------------------------------------------------------------

export default Auth;



// End of file
